import React from 'react';
import {
  getProviderColor,
  ProviderColorKey,
} from '../../utils/providerColors';
import { CurrencyCode, getCurrencySymbol } from '../../utils/currency';

/** Narrow an arbitrary currency string to a known CurrencyCode, defaulting to USD */
function toCurrencyCode(currency: string): CurrencyCode {
  return (['USD', 'INR', 'EUR', 'GBP'] as const).includes(currency as CurrencyCode)
    ? (currency as CurrencyCode)
    : 'USD';
}

/**
 * ProviderBreakdownBar component
 * Implements REQ-012-HYBRID: Stacked provider breakdown bar in the hero section
 * showing a visual split (e.g., 52% AWS, 31% Azure, 17% GCP).
 *
 * Features:
 * - Horizontal stacked bar colored by provider brand colors
 * - Percentage labels on each segment
 * - Provider legend with color dots, percentage, and formatted cost
 * - Min-width for tiny segments so labels remain readable
 * - Smooth CSS transitions when data changes
 * - Dark mode support
 * - WCAG 2.1 AA accessible (aria-label, sr-only text, role="img")
 */

/** Data for a single provider in the breakdown */
export interface ProviderBreakdownEntry {
  provider: string;
  cost: number;
  percentage: number;
}

export interface ProviderBreakdownBarProps {
  /** Array of provider breakdown data */
  providers: ProviderBreakdownEntry[];
  /** Total cost across all providers (optional, computed from providers if not given) */
  totalCost?: number;
  /** Additional CSS class */
  className?: string;
  /** Currency to display amounts in — native currency for single-provider views, USD when mixing providers */
  currency?: string;
}

/** Format cost as compact currency string */
function formatCompactCost(value: number, currency: string = 'USD'): string {
  const symbol = getCurrencySymbol(toCurrencyCode(currency));
  if (value >= 1_000_000) {
    return `${symbol}${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${symbol}${(value / 1_000).toFixed(1)}K`;
  }
  return `${symbol}${value.toFixed(0)}`;
}

/** Normalize a provider name to a canonical key */
function normalizeProviderKey(provider: string): ProviderColorKey | null {
  const lower = provider.toLowerCase().trim();
  const keyMap: Record<string, ProviderColorKey> = {
    aws: 'aws',
    azure: 'azure',
    gcp: 'gcp',
    'amazon web services': 'aws',
    'microsoft azure': 'azure',
    'google cloud': 'gcp',
    'google cloud platform': 'gcp',
  };
  return keyMap[lower] ?? null;
}

/**
 * ProviderBreakdownBar displays a horizontal stacked bar showing the cost split
 * between cloud providers. Each segment is colored with the provider's brand color.
 *
 * WCAG 2.1 AA compliant:
 * - role="img" with aria-label describing the full breakdown
 * - Screen reader-only text for each segment
 * - Sufficient color contrast via brand colors on neutral backgrounds
 */
export const ProviderBreakdownBar: React.FC<ProviderBreakdownBarProps> = ({
  providers,
  totalCost,
  className = '',
  currency = 'USD',
}) => {
  // Filter out providers with 0% and sort descending by percentage
  const validProviders = providers
    .filter((p) => p.percentage > 0 && p.cost > 0)
    .sort((a, b) => b.percentage - a.percentage);

  // Empty state: nothing to render
  if (validProviders.length === 0) {
    return null;
  }

  const computedTotal = totalCost ?? validProviders.reduce((sum, p) => sum + p.cost, 0);

  // Build the aria-label describing the full breakdown
  const breakdownDescription = validProviders
    .map(
      (p) =>
        `${p.provider.toUpperCase()} ${Math.round(p.percentage)}% (${formatCompactCost(p.cost, currency)})`
    )
    .join(', ');

  return (
    <div className={`w-full ${className}`}>
      {/* Stacked bar */}
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-secondary"
        role="img"
        aria-label={`Provider cost breakdown: ${breakdownDescription}. Total: ${formatCompactCost(computedTotal, currency)}`}
      >
        {validProviders.map((entry, index) => {
          const key = normalizeProviderKey(entry.provider);
          const color = key ? getProviderColor(key) : '#6B7280';

          // Minimum width of 4% for very small segments so they remain visible
          const displayWidth = Math.max(entry.percentage, 4);

          // Rounded corners only on first/last segments
          const isFirst = index === 0;
          const isLast = index === validProviders.length - 1;
          const roundedClasses = [
            isFirst ? 'rounded-l-full' : '',
            isLast ? 'rounded-r-full' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={entry.provider}
              className={`relative h-full transition-all duration-500 ease-in-out ${roundedClasses}`}
              style={{
                width: `${displayWidth}%`,
                backgroundColor: color,
              }}
              title={`${entry.provider.toUpperCase()}: ${Math.round(entry.percentage)}% — ${formatCompactCost(entry.cost, currency)}`}
            >
              {/* Screen reader text for each segment */}
              <span className="sr-only">
                {entry.provider.toUpperCase()}: {Math.round(entry.percentage)} percent,{' '}
                {formatCompactCost(entry.cost, currency)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Legend row below bar */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {validProviders.map((entry) => {
          const key = normalizeProviderKey(entry.provider);
          const color = key ? getProviderColor(key) : '#6B7280';

          return (
            <div
              key={entry.provider}
              className="flex items-center gap-1.5 text-xs text-foreground/80"
            >
              {/* Color dot */}
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden="true"
              />
              {/* Provider name + percentage */}
              <span className="font-medium">
                {entry.provider.toUpperCase()}
              </span>
              <span className="text-muted-foreground">
                {Math.round(entry.percentage)}%
              </span>
              {/* Formatted cost */}
              <span className="text-muted-foreground">
                ({formatCompactCost(entry.cost, currency)})
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
